import React, { useMemo, useState } from 'react';
import { Modal } from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { useGetPlans } from '../../hooks/api/plan';
import apiClient from '../../apiClient';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { Plan } from '../../hooks/api/user';
import type { LessonAccessLevel } from '../../hooks/api/lesson';
import './payment.scss';

// Single source of truth for how each access tier reads and looks.
const ACCESS_LEVEL_ORDER: LessonAccessLevel[] = ['normal', 'pro', 'morning'];

const ACCESS_LEVEL_META: Record<LessonAccessLevel, { label: string; color: string }> = {
  normal: { label: 'Normal', color: '#6b7785' }, // slate — baseline access
  pro: { label: 'Pro', color: '#c08a2d' }, // gold — premium access
  morning: { label: 'Mañana', color: '#3a86c8' }, // sky — morning sessions
};

/** Stable, ordered, de-duplicated access levels for a plan. */
function orderedLevels(plan: Plan): LessonAccessLevel[] {
  const allowed = plan.allowedLessonAccessLevels ?? [];
  return ACCESS_LEVEL_ORDER.filter((level) => allowed.includes(level));
}

function Dots({ levels }: { levels: LessonAccessLevel[] }) {
  return (
    <span className="pp-tier__dots">
      {levels.map((level) => (
        <span
          key={level}
          className="pp-dot"
          title={ACCESS_LEVEL_META[level].label}
          style={{ backgroundColor: ACCESS_LEVEL_META[level].color }}
        />
      ))}
    </span>
  );
}

export default function PaymentPage() {
  const { data: allPlans, isLoading } = useGetPlans();
  const { user } = useCurrentUser();

  const [open, setOpen] = useState(false);

  const redirectToCheckout = async (planId: string) => {
    try {
      const { data } = await apiClient.get<string>(`/checkout-url?planId=${planId}`);
      window.location.replace(data);
    } catch (e) {
      console.error(e);
    }
  };

  // Group sellable plans (order > 0) by their access-level signature, so the
  // page reads as tiers — "Normal", "Pro", "Mañana", or bundles like "Normal · Pro".
  const groups = useMemo(() => {
    const plans = (allPlans ?? []).filter((plan) => plan.order > 0);
    const byKey = new Map<string, { key: string; levels: LessonAccessLevel[]; plans: Plan[] }>();

    plans.forEach((plan) => {
      const levels = orderedLevels(plan);
      const key = levels.join('|') || 'general';
      if (!byKey.has(key)) {
        byKey.set(key, { key, levels, plans: [] });
      }
      byKey.get(key)!.plans.push(plan);
    });

    const rank = (level: LessonAccessLevel) => ACCESS_LEVEL_ORDER.indexOf(level);

    return Array.from(byKey.values())
      .map((group) => ({
        ...group,
        plans: [...group.plans].sort((a, b) => a.order - b.order || a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => {
        // Broadest bundles first (most access levels), narrower tiers afterwards.
        if (a.levels.length !== b.levels.length) {
          return b.levels.length - a.levels.length;
        }
        const len = Math.max(a.levels.length, b.levels.length);
        for (let i = 0; i < len; i += 1) {
          const diff = rank(a.levels[i] ?? 'normal') - rank(b.levels[i] ?? 'normal');
          if (diff !== 0) return diff;
        }
        return 0;
      });
  }, [allPlans]);

  const groupLabel = (levels: LessonAccessLevel[]) =>
    levels.length === 0
      ? 'General'
      : levels.map((level) => ACCESS_LEVEL_META[level].label).join(' · ');

  const hasPlan = !!user?.plan;
  const currentPlanId = user?.plan?.id;

  // Stable, increasing entrance delay across all cards regardless of grouping.
  let cardIndex = 0;

  return (
    <div className="payment-page">
      <header className="pp-hero">
        <span className="pp-hero__kicker">Membresías</span>
        <h1 className="pp-hero__title">Elige tu plan</h1>
        <p className="pp-hero__subtitle">
          Algunos planes solo incluyen un tipo de clase en específico.
        </p>
      </header>

      {hasPlan && (
        <div className="pp-current">
          <span className="pp-current__pulse" />
          <span>
            Tu plan activo es <strong>{user?.plan?.name}</strong>
          </span>
        </div>
      )}

      {isLoading && (
        <div>
          <div className="pp-skeleton" />
          <div className="pp-skeleton" />
          <div className="pp-skeleton" />
        </div>
      )}

      {!isLoading &&
        groups.map((group) => (
          <section className="pp-tier" key={group.key}>
            <div className="pp-tier__head">
              <Dots levels={group.levels} />
              <span className="pp-tier__label">{groupLabel(group.levels)}</span>
              <span className="pp-tier__rule" />
            </div>

            {group.plans.map((plan) => {
              const isCurrent = plan.id === currentPlanId;
              const period = plan.mode === 'subscription' ? '/ mes' : 'pago único';
              cardIndex += 1;
              return (
                <article
                  key={plan.id}
                  className={`pp-card${isCurrent ? ' pp-card--active' : ''}`}
                  style={{ animationDelay: `${0.08 + cardIndex * 0.05}s` }}
                >
                  <div className="pp-card__head">
                    <div>
                      <h3 className="pp-card__name">{plan.name}</h3>
                      <span className="pp-card__minidots">
                        {orderedLevels(plan).map((level) => (
                          <span
                            key={level}
                            className="pp-dot"
                            title={ACCESS_LEVEL_META[level].label}
                            style={{ backgroundColor: ACCESS_LEVEL_META[level].color }}
                          />
                        ))}
                      </span>
                    </div>
                    {isCurrent && <span className="pp-card__badge">Activo</span>}
                  </div>

                  <div className="pp-card__price">
                    <span className="pp-card__amount">{plan.price}€</span>
                    <span className="pp-card__period">{period}</span>
                  </div>

                  <div className="pp-card__meta">
                    <EventAvailableIcon />
                    {plan.lessons === 100
                      ? 'Clases ilimitadas'
                      : `${plan.lessons} ${plan.lessons === 1 ? 'clase' : 'clases'}`}
                  </div>

                  {isCurrent ? (
                    <button type="button" className="pp-cta pp-cta--ghost" disabled>
                      Plan actual
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="pp-cta"
                      onClick={() => redirectToCheckout(plan.id)}
                      disabled={hasPlan}
                    >
                      Empezar
                    </button>
                  )}
                </article>
              );
            })}
          </section>
        ))}

      {hasPlan && (
        <button type="button" className="pp-cancel" onClick={() => setOpen(true)}>
          Darme de baja
        </button>
      )}

      <Modal open={open} onClose={() => setOpen(false)}>
        <div className="pp-modal">
          <h2 className="pp-modal__title">Darse de baja</h2>
          <p className="pp-modal__text">
            Para tramitar la baja, escríbenos por WhatsApp y lo gestionamos contigo.
          </p>
          <a
            className="pp-modal__wa"
            href="https://wa.me/34722613752"
            target="_blank"
            rel="noreferrer"
          >
            <WhatsAppIcon fontSize="small" />
            Escribir por WhatsApp
          </a>
        </div>
      </Modal>
    </div>
  );
}
